// Sign in, sign up, password recovery and the confirmation of the email. They share how a form behaves
// (forms.tsx; the messages and where each one goes are in form-messages.ts): the fields say what is wrong
// when they are left or the form is sent (never on load, never from the first character, and the message goes
// when the value is right), the system speaks after its answer, one alert that takes the focus and says how
// to go on, the form keeps what was typed, a send happens once, the button comes back after a failure, a wait
// is told in real seconds, and nothing is confirmed before the system confirms it.
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Form } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';
import { meQuery } from '../api';
import { runAuth } from '../auth-call';
import { authClient } from '../auth-client';
import { useAuthForm } from '../auth-form';
import { CooldownNotice, FormError, SubmitButton } from '../forms';
import { GoogleProblem, GoogleSignIn } from '../google-sign-in';
import i18n from '../i18n';
import { PASSWORD_LIMITS } from '../password-rules';
import { Button, buttonClass, Field, Message, Page, PasswordField } from '../ui';
import { nameFromEmail, publicPage, readForm, splitLinkFailure, verificationCallback } from '../util';
import { VerificationScreen } from './verification';

export function LoginPage() {
  const { t } = useTranslation();
  const { redirect, error: googleError } = useSearch({ from: '/login' });
  // A confirmation link that failed brings the person here: the page they wanted is kept and the failure is told.
  const { target, linkFailed } = splitLinkFailure(redirect);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const form = useAuthForm();
  // The account whose password was right but whose email is not verified yet: the screen of its verification.
  const [unverified, setUnverified] = useState<{ email: string; password: string } | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { email = '', password = '' } = readForm(event);
    await form.run(async () => {
      const outcome = await runAuth((fetchOptions) =>
        authClient.signIn.email({ email, password, fetchOptions }),
      );
      // Only a right password gets here with this answer: a wrong one says nothing of the account's state.
      if (outcome.kind === 'unverified') {
        form.show({ kind: 'ok' });
        setUnverified({ email, password });
        return;
      }
      if (form.show(outcome, { credentials: t('errors.invalidCredentials') })) return;
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      await navigate({ href: target ?? '/' });
    });
  }

  // Two views: the form stays in place (hidden) while the verification screen is shown, so what was typed is
  // there when the person goes back.
  return (
    <>
      {unverified && (
        <VerificationScreen
          email={unverified.email}
          password={unverified.password}
          next={target}
          state="earlier"
          onBack={() => {
            setUnverified(null);
            // The button that had the focus is gone: it goes back to the form.
            window.setTimeout(
              () => document.querySelector<HTMLInputElement>('input[name=email]')?.focus(),
              0,
            );
          }}
        />
      )}
      <div hidden={Boolean(unverified)}>
        <Page title={t('login.title')} brand>
          <p className="page-subtitle">{t('login.subtitle')}</p>
          <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
            {linkFailed && !form.problem && (
              <Message tone="error">
                {t('forms.verifyLink')}{' '}
                <Link to="/resend-verification" search={{ redirect: target }}>
                  {t('verify.done.request')}
                </Link>
              </Message>
            )}
            {!unverified && !form.problem && !linkFailed && (
              <GoogleProblem code={googleError} redirect={target} />
            )}
            {!unverified && form.problem && (
              <FormError message={form.problem.message} attempt={form.problem.attempt} />
            )}
            {!unverified && <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />}
            <GoogleSignIn mode="login" target={target} placement="above" />
            <Field name="email" type="email" label={t('login.email')} autoComplete="username" isRequired />
            <PasswordField name="password" label={t('login.password')} mode="current" isRequired />
            <p className="-mt-3">
              <Link to="/forgot-password">{t('login.forgot')}</Link>
            </p>
            <SubmitButton
              label={t('login.submit')}
              busyLabel={t('login.submitting')}
              busy={form.busy}
              waiting={form.cooldown.active}
            />
          </Form>
          <p className="mt-6">
            {t('login.noAccount')}{' '}
            <Link to="/signup" search={{ redirect: target }}>
              {t('login.signup')}
            </Link>
          </p>
        </Page>
      </div>
    </>
  );
}

/** A link to the public website that opens in a new tab, so the form keeps what was typed. */
function WebsiteLink({ href, children }: { href: string; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
      <span className="sr-only"> ({t('app.newTab')})</span>
    </a>
  );
}

/**
 * Creating an account asks for an email, a password and acceptance of privacy and terms. The account
 * stays pending until the email is verified (Better Auth's `requireEmailVerification`): until then it
 * cannot sign in.
 */
export function SignupPage() {
  const { t, i18n } = useTranslation();
  const { redirect, error: googleError } = useSearch({ from: '/signup' });
  const form = useAuthForm();
  const [passwordError, setPasswordError] = useState<string | null>(null);
  // Privacy box: empty until ticked; the message only after the field was left or the form was sent.
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyStarted, setPrivacyStarted] = useState(false);
  const [privacyLeft, setPrivacyLeft] = useState(false);
  const [privacySent, setPrivacySent] = useState(false);
  const privacyError = (privacyLeft || privacySent) && !privacyAccepted ? t('forms.privacy.required') : null;
  // The account that was just created: its verification screen (and what the service said of the first email).
  const [created, setCreated] = useState<{
    email: string;
    password: string;
    sent: boolean;
    wait?: number;
  } | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { email = '', password = '' } = readForm(event);
    // A new answer about the password is shown as new: the last one is taken away first.
    setPasswordError(null);
    await form.run(async () => {
      const outcome = await runAuth((fetchOptions) =>
        authClient.signUp.email({
          name: nameFromEmail(email),
          email,
          password,
          locale: i18n.language,
          // The link in the email lands on the page that says what happened; the page the person wanted
          // (e.g. an invitation) travels with it.
          callbackURL: verificationCallback(redirect),
          fetchOptions,
        }),
      );
      if (outcome.kind === 'password') {
        // The password goes back to its field, with the rule it broke; nothing else is lost.
        setPasswordError(t(`password.errors.${outcome.rule}`, PASSWORD_LIMITS));
        form.setProblem(null);
        return;
      }
      setPasswordError(null);
      if (form.show(outcome, { exists: t('errors.userExists') })) return;
      // The account exists either way; whether the first email went out is what the service said.
      setCreated({
        email,
        password,
        sent: outcome.kind === 'ok' && outcome.emailSent !== false,
        wait: outcome.kind === 'ok' ? outcome.wait : undefined,
      });
    });
  }

  return (
    <>
      {created && (
        <VerificationScreen
          email={created.email}
          password={created.password}
          next={redirect}
          state={created.sent ? 'sent' : 'notSent'}
          wait={created.wait}
        />
      )}
      <div hidden={Boolean(created)}>
        <Page title={t('signup.title')} brand>
          <p className="mb-6 text-ink-muted">{t('signup.benefit')}</p>
          <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
            {!form.problem && <GoogleProblem code={googleError} redirect={redirect} />}
            {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
            <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
            {/* Same box as for the email: without it ticked the button does not leave, it takes the person to it. */}
            <GoogleSignIn
              mode="signup"
              target={redirect}
              placement="above"
              ready={privacyAccepted}
              onNotReady={() => {
                setPrivacySent(true);
                document.querySelector<HTMLInputElement>('input[name=privacy]')?.focus();
              }}
            />
            <Field
              name="email"
              type="email"
              label={t('signup.email')}
              autoComplete="username"
              spellCheck="false"
              isRequired
            />
            <PasswordField
              name="password"
              label={t('signup.password')}
              mode="new"
              error={passwordError}
              isRequired
            />
            {/* Same idea as the contact form on the public site: a required box before the button; the
            documents open in another tab so nothing typed is lost and opening them does not tick the box. */}
            <div className="flex flex-col gap-2">
              <label className="follow-check statement-confirm">
                <input
                  type="checkbox"
                  name="privacy"
                  required
                  checked={privacyAccepted}
                  aria-invalid={privacyError ? true : undefined}
                  aria-describedby={privacyError ? 'signup-privacy-error' : undefined}
                  onChange={(event) => {
                    setPrivacyAccepted(event.target.checked);
                    setPrivacyStarted(true);
                  }}
                  onBlur={() => {
                    if (privacyStarted) setPrivacyLeft(true);
                  }}
                  onInvalid={(event) => {
                    // Our own message under the box; the browser's bubble is not used.
                    event.preventDefault();
                    setPrivacySent(true);
                  }}
                />
                <span>
                  <Trans
                    i18nKey="signup.privacy.label"
                    components={{
                      privacy: <WebsiteLink href={publicPage(i18n.language, 'privacy')} />,
                      terms: <WebsiteLink href={publicPage(i18n.language, 'terms')} />,
                    }}
                  />
                  <span aria-hidden="true"> *</span>
                </span>
              </label>
              {privacyError && (
                <p id="signup-privacy-error" role="alert" className="text-sm font-bold text-error">
                  {privacyError}
                </p>
              )}
              {/* The documents exist in Spanish and in English; in another language the person is told which one they open. */}
              {!['es', 'en'].includes(i18n.language) && (
                <p className="text-sm text-ink-muted">{t('signup.privacy.language')}</p>
              )}
            </div>
            <p className="font-bold">{t('signup.verify')}</p>
            <SubmitButton
              label={t('signup.submit')}
              busyLabel={t('signup.submitting')}
              busy={form.busy}
              waiting={form.cooldown.active}
            />
          </Form>
          <p className="mt-6">
            {t('signup.haveAccount')}{' '}
            <Link to="/login" search={{ redirect }}>
              {t('signup.login')}
            </Link>
          </p>
        </Page>
      </div>
    </>
  );
}

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const form = useAuthForm();
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { email = '' } = readForm(event);
    await form.run(async () => {
      // The answer is the same whether the account exists or not: only that the request was made.
      const outcome = await runAuth((fetchOptions) =>
        authClient.requestPasswordReset({ email, redirectTo: '/reset-password', fetchOptions }),
      );
      if (form.show(outcome)) return;
      setSent(true);
    });
  }

  return (
    <Page title={t(sent ? 'forgot.sentTitle' : 'forgot.title')} brand>
      {sent ? (
        <Message tone="ok">{t('forgot.sent')}</Message>
      ) : (
        <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
          {form.problem && <FormError message={form.problem.message} attempt={form.problem.attempt} />}
          <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
          <p>{t('forgot.intro')}</p>
          <Field name="email" type="email" label={t('forgot.email')} autoComplete="username" isRequired />
          <SubmitButton
            label={t('forgot.submit')}
            busyLabel={t('forgot.submitting')}
            busy={form.busy}
            waiting={form.cooldown.active}
          />
        </Form>
      )}
      <p className="mt-6">
        <Link to="/login">{t('forgot.back')}</Link>
      </p>
    </Page>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token, error: linkError } = useSearch({ from: '/reset-password' });
  const form = useAuthForm();
  const [done, setDone] = useState(false);
  // The account is still pending its email verification: changing the password does not change that.
  const [pending, setPending] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  // A link that is not valid is known when the page opens: it is told, with the way to ask for another.
  const brokenLink = Boolean(linkError) || !token;
  const [linkUsed, setLinkUsed] = useState(false);
  const another = (
    <Link to="/forgot-password" className={buttonClass('secondary')}>
      {t('reset.another')}
    </Link>
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    const { password = '' } = readForm(event);
    if (!token) return;
    setPasswordError(null);
    await form.run(async () => {
      const outcome = await runAuth((fetchOptions) =>
        authClient.resetPassword({ newPassword: password, token, fetchOptions }),
      );
      if (outcome.kind === 'password') {
        setPasswordError(t(`password.errors.${outcome.rule}`, PASSWORD_LIMITS));
        form.setProblem(null);
        return;
      }
      setPasswordError(null);
      if (outcome.kind === 'link') setLinkUsed(true);
      if (form.show(outcome, { link: t('reset.invalid') })) return;
      setPending(outcome.kind === 'ok' && Boolean(outcome.pending));
      setDone(true);
    });
  }

  return (
    <Page title={t(done ? 'reset.doneTitle' : 'reset.title')} brand>
      {done ? (
        <>
          <Message tone="ok">{t('reset.done')}</Message>
          {pending && <p className="mt-4 font-bold">{t('reset.pending')}</p>}
          <p className="mt-6">
            <Link to="/login" className={buttonClass('primary')}>
              {t('reset.login')}
            </Link>
          </p>
        </>
      ) : (
        <Form onSubmit={submit} validationBehavior="native" className="flex flex-col gap-5">
          {brokenLink ? (
            <FormError message={t('reset.invalid')} attempt={0}>
              {another}
            </FormError>
          ) : (
            form.problem && (
              <FormError message={form.problem.message} attempt={form.problem.attempt}>
                {linkUsed && another}
              </FormError>
            )
          )}
          <CooldownNotice cooldown={form.cooldown} seconds={form.waitSeconds} />
          <PasswordField
            name="password"
            label={t('reset.password')}
            mode="new"
            error={passwordError}
            isRequired
            isDisabled={brokenLink}
          />
          <SubmitButton
            label={t('reset.submit')}
            busyLabel={t('reset.submitting')}
            busy={form.busy}
            waiting={form.cooldown.active || brokenLink}
          />
        </Form>
      )}
    </Page>
  );
}
